import { createStoreLogger } from '../helpers/loggers';
import notificationStore from "./Notification/notificationStore";
import type {  AxiosResponse } from 'axios';


export type UnwrapResponseFn<F> =
  F extends (...args: infer A) => Promise<infer R>
    ? R extends AxiosResponse<infer D>
      ? (...args: A) => Promise<D>
      : F
    : F;

/**
 * Take an API client type T, and transform each method with UnwrapResponseFn.
 */
export type WrappedApiClient<T> = {
  [K in keyof T]: UnwrapResponseFn<T[K]>;
};


// TClient is whatever your autogenerated Swagger client is
export abstract class BaseStore<TClient extends object> {
  public readonly api: WrappedApiClient<TClient>;
  public readonly apiUnwrapped: TClient;

  protected logger;

  constructor(apiClient: TClient) {
    this.apiUnwrapped = apiClient;
    this.logger = createStoreLogger(this.constructor.name);    

    // Wrap all methods on the specific client instance
    const handler: ProxyHandler<TClient> = {
      get: (target, prop: string | symbol) => {
        const orig = (target as any)[prop];
        if (typeof orig !== "function") return orig;
        return async (...args: any[]) => {
          const action = String(prop);
          this.logger.info(`→ ${action}()`, args);
          const start = performance.now();
          try {
            const { data } = await orig.apply(target, args);
            const ms = (performance.now() - start).toFixed(2);
            this.logger.debug(`✓ ${action}() [${ms}ms]:`, data);
            return data;
          } catch (err: any) {
            this.logger.error(`✗ ${action}():`, err);
            const msg = err.response?.data?.message ?? `Error in ${action}`;
            notificationStore.showMessage(msg, "error", err);
            throw err;
          }
        };
      }
    };

    // Cast the proxy back to the original client type
    this.api = new Proxy(apiClient, handler) as WrappedApiClient<TClient>;
  }
}
